







































































































import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import Rep from '@/_shared/entities/rep/rep.entity';
import UserPhoto from '@/_shared/components/rep/user-photo.component.vue';
import YourWebsiteService from '@/profile/your-website/services/your-website.service';
import RepSite from '@/_shared/entities/rep/rep-site.entity';
import LogService from '@/_shared/services/log/log.service';
import Editor from '@tinymce/tinymce-vue';
import DisplayFlags from '@/profile/your-website/entities/display-flags.entity';
import SessionMessageService from '@/_shared/services/session-message.service';
import { BFormFile } from 'bootstrap-vue';

@Component({
  components: {
    UserPhoto,
    Editor,
    BFormFile,
  },
})
export default class YourWebsiteView extends Vue {
  private repSite: RepSite | null = null;

  private displayFlags: DisplayFlags | null = null;

  private siteContent: string | null = null;

  private selectedPicture: File | null = null;

  private sitePicture = '';

  private hasFinishedLoading = false;

  private CHARACTER_LIMIT = 1000;

  $refs!: {
    sitePictureInput: HTMLFormElement;
  }

  get rep(): Rep {
    return this.$store.state.RepStore.rep;
  }

  get siteUrl(): string {
    return `https://${this.rep.url}.kyani.com`;
  }

  get showCompanyNameToggle(): boolean {
    if (!this.hasFinishedLoading) return false;
    const companyName = this.rep.company ?? '';
    return companyName.length > 0;
  }

  get editorKey() {
    return process.env.VUE_APP_EDITOR_KEY;
  }

  get editorConfig() {
    return {
      height: '600px',
      width: '100%',
      // eslint-disable-next-line @typescript-eslint/camelcase
      max_width: '100%',
      // eslint-disable-next-line @typescript-eslint/camelcase
      max_chars: 1000,
      menubar: false,
      toolbar: 'bold italic underline strikethrough |'
        + ' numlist bullist | undo redo | removeformat |'
        + ' formatselect | alignleft aligncenter alignright alignjustify |'
        + ' outdent indent | image link',
      plugins: 'autoresize image link advlist lists',
      // eslint-disable-next-line @typescript-eslint/camelcase
      content_css: [
        'https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,200;0,300;0,400;0,600;0,700;0,'
        + '800;0,900;1,200;1,300;1,400;1,600;1,700;1,800;1,900&display=swap',
      ],
      // eslint-disable-next-line @typescript-eslint/camelcase
      content_style: 'body { font-family: "Nunito", sans-serif; font-size: 14px; color: #666666; }',
      elementpath: false,
      mobile: {
        plugins: ['autoresize', 'image', 'link', 'advlist', 'lists'],
        toolbar: [
          'bold italic underline strikethrough | numlist bullist | undo redo',
          'removeformat | formatselect | alignleft aligncenter alignright alignjustify',
          'outdent indent | image link',
        ],
      },
    };
  }

  get disableSaveButton(): boolean {
    if (this.siteContent === null) return false;
    return this.removeHtmlTags(this.siteContent).length >= this.CHARACTER_LIMIT;
  }

  public checkCharacterLimit(event: any, editor: any): void {
    const contentLength = this.removeHtmlTags(editor.getContent()).length;
    if (event.which > 46 && (contentLength + 1) > this.CHARACTER_LIMIT) {
      event.preventDefault();
    }
  }

  public updateStatusbar(event: any, editor: any): void {
    const contentLength = this.removeHtmlTags(editor.getContent()).length;
    const container = editor.getContainer().querySelector('.tox-statusbar__text-container');
    container.innerHTML = `<div> Characters: ${contentLength}/1000 </div>`;
  }

  public removeHtmlTags(originalText: string): string {
    return originalText.replace(/<[^>]*>/gm, '');
  }

  private async created() {
    await this.$store.dispatch('showLoading');
    await this.retrieveRepSite();
    await this.retrieveDisplayFlags();
    await this.retrieveSiteContent();
    this.$emit('done-loading');
    this.hasFinishedLoading = true;
    await this.$store.dispatch('hideLoading');
  }

  private selectPicture(): void {
    this.$refs.sitePictureInput.$el.childNodes[0].click();
  }

  private retrieveRepSite() {
    return YourWebsiteService.retrieveRepSite(this.rep.did)
      .then((repSite) => {
        this.repSite = repSite;
      });
  }

  private retrieveDisplayFlags() {
    return YourWebsiteService.retrieveDisplayFlags(this.rep.did)
      .then((displayFlags) => {
        this.displayFlags = displayFlags;
      });
  }

  private retrieveSiteContent() {
    return YourWebsiteService.retrieveRepSiteInfo(this.rep.did)
      .then((repSiteInfo) => {
        this.siteContent = repSiteInfo.content;
        this.sitePicture = repSiteInfo.picture;
      });
  }

  private changeSitePicture(): void {
    if (this.selectedPicture === null) {
      return;
    }

    this.$store.dispatch('showLoading');
    YourWebsiteService.changeSitePicture(this.rep.did, this.selectedPicture)
      .then(() => {
        this.sitePicture = URL.createObjectURL(this.selectedPicture);
        this.showSuccessMessage('PROFILE.TOAST_SUCCESS_MESSAGE');
      })
      .catch(() => {
        this.showErrorMessage('PROFILE.TOAST_ERROR_MESSAGE');
      })
      .finally(() => {
        this.$store.dispatch('hideLoading');
      });
  }

  private async saveChanges() {
    await this.$store.dispatch('showLoading');
    await this.saveDisplayFlags();
    await this.saveSiteContent();
    await this.$store.dispatch('hideLoading');
    this.showSessionMessages();
  }

  private saveDisplayFlags() {
    if (this.displayFlags === null) {
      return null;
    }

    return YourWebsiteService.saveDisplayFlags(
      this.rep.did,
      this.displayFlags.companyNameDisplayFlag,
      this.displayFlags.emailDisplayFlag,
      this.displayFlags.sitePictureDisplayFlag,
    )
      .then(() => {
        SessionMessageService.registerSuccessfulMessage('PROFILE.TOAST_SUCCESS_MESSAGE');
      })
      .catch(() => {
        SessionMessageService.registerErrorMessage('PROFILE.TOAST_ERROR_MESSAGE');
      });
  }

  private saveSiteContent() {
    if (this.siteContent === null) return null;
    if (this.siteContent.length === 0) {
      this.siteContent = ' ';
    }

    return YourWebsiteService.saveSiteContent(this.rep.did, this.siteContent)
      .then(() => {
        SessionMessageService.registerSuccessfulMessage('PROFILE.TOAST_SUCCESS_MESSAGE');
      })
      .catch(() => {
        SessionMessageService.registerErrorMessage('PROFILE.TOAST_ERROR_MESSAGE');
      });
  }

  private showSessionMessages(): void {
    const sessionMessages = SessionMessageService.getMessages();
    SessionMessageService.clear();
    for (let i = 0; i < sessionMessages.length; i += 1) {
      const message = sessionMessages[i];
      if (message.type === 'success') {
        this.showSuccessMessage(message.content);
      } else {
        this.showErrorMessage(message.content);
      }
    }
  }

  private showSuccessMessage(message: string): void {
    LogService.toastrSuccess(
      this.$t(message).toString(),
      this.$t('PROFILE.TOAST_SUCCESS_HEADER').toString(),
    );
  }

  private showErrorMessage(message: string): void {
    LogService.toastrError(
      this.$t(message).toString(),
      this.$t('PROFILE.TOAST_ERROR_HEADER').toString(),
    );
  }
}
